<template>
  <Select
    ref="selectRef"
    :model-value="countryScope"
    :options="options"
    optionLabel="label"
    optionValue="value"
    class="sidebar-country-switcher"
    :class="{ 'sidebar-country-switcher--rail': isRailMode }"
    :aria-label="`Workspace country: ${selectedLabel}. Switch country`"
    :title="isRailMode ? `CRC Transport · ${selectedLabel}` : undefined"
    appendTo="body"
    overlayClass="sidebar-country-menu"
    :overlay-style="{ zIndex: 3000 }"
    :filter="options.length > 7"
    filterPlaceholder="Find a country"
    scrollHeight="18rem"
    :pt="{ label: { class: 'sidebar-country-switcher__value' }, dropdown: { class: 'sidebar-country-switcher__dropdown' } }"
    @update:model-value="setCountryScope"
    @show="emit('menu-open-change', true)"
    @hide="emit('menu-open-change', false)"
  >
    <template #value>
      <span class="sidebar-brand__mark sidebar-country-switcher__mark" aria-hidden="true">
        <span v-if="badge">{{ badge }}</span>
        <i v-else class="pi pi-globe" />
      </span>
      <span class="sidebar-brand__copy sidebar-country-switcher__copy">
        <span class="sidebar-country-switcher__company">CRC Transport</span>
        <span class="sidebar-country-switcher__country">{{ selectedLabel }}</span>
      </span>
    </template>
    <template #dropdownicon><i class="pi pi-chevron-down" aria-hidden="true" /></template>
    <template #header>
      <div class="sidebar-country-menu__header">
        <span>Workspace country</span>
        <small>Choose which data you see</small>
      </div>
    </template>
    <template #option="{ option }">
      <span class="sidebar-country-menu__badge" aria-hidden="true">
        <span v-if="countryBadge(option.value)">{{ countryBadge(option.value) }}</span>
        <i v-else class="pi pi-globe" />
      </span>
      <span class="sidebar-country-menu__label">{{ option.label }}</span>
      <i v-if="option.value === countryScope" class="pi pi-check sidebar-country-menu__check" aria-hidden="true" />
    </template>
    <template #footer>
      <div class="sidebar-country-menu__footer">
        <template v-if="catalogError">
          <span>Country list unavailable</span>
          <button type="button" @click.stop="refreshCatalog">Retry</button>
        </template>
        <span v-else>Selection applies across your workspace</span>
      </div>
    </template>
  </Select>
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount, ref, watch } from 'vue';
import Select from 'primevue/select';
import { buildCountryScopeOptions, countryScope, setCountryScope } from '@/composables/useCountryScopeFilter';
import { countryOptionLabel, countriesMatch } from '@/utils/countryCatalog';
import { catalogCountriesRef, countryCatalogListOptions, loadCountryCatalog } from '@/utils/countryCatalogState';
import { countryBadge } from '@/utils/workspaceCountry';
import { useInboxSummary } from '@/composables/useInboxSummary';

const props = defineProps({
  isRailMode: { type: Boolean, default: false },
  isSidebarVisible: { type: Boolean, default: true }
});
const selectRef = ref(null);
const emit = defineEmits(['menu-open-change']);
const catalogError = ref(false);
const selectedLabel = computed(() => countryScope.value === 'all' ? 'All countries' : countryOptionLabel(countryScope.value));
const badge = computed(() => countryBadge(countryScope.value));
const options = computed(() => {
  const catalog = countryCatalogListOptions(countryScope.value === 'all' ? [] : [countryScope.value]);
  return buildCountryScopeOptions([], catalog.extra, catalog)
    .map((option) => option.value === 'all' ? { ...option, label: 'All countries' } : option);
});

async function refreshCatalog() {
  try {
    await loadCountryCatalog({ force: catalogError.value });
    catalogError.value = false;
  } catch { catalogError.value = true; }
}

const inbox = useInboxSummary();
watch(catalogCountriesRef, (rows, previous) => {
  const selected = previous.find((row) => countriesMatch(row.name, countryScope.value));
  if (!selected) return;
  const updated = rows.find((row) => Number(row.id) === Number(selected.id));
  setCountryScope(updated?.name || 'all');
});
watch(countryScope, () => {
  inbox.reset();
  inbox.refresh();
});
watch(() => props.isSidebarVisible, (visible) => {
  if (!visible) selectRef.value?.hide();
});
onBeforeUnmount(() => emit('menu-open-change', false));
onMounted(refreshCatalog);
</script>

<style>
.sidebar-country-switcher.p-select {
  flex: 1 1 auto;
  width: 100%;
  min-width: 2.75rem;
  height: 2.75rem;
  min-height: 2.75rem;
  gap: 0.375rem;
  border: 1px solid transparent;
  border-radius: 0.75rem;
  background: transparent;
  box-shadow: none;
  padding: 0.1875rem;
  margin-left: calc(-0.25rem * (1 - var(--sidebar-header-collapse, 0)));
  overflow: hidden;
  color: var(--crc-sidebar-text);
  transition: background 160ms ease, border-color 160ms ease;
}
.sidebar-country-switcher.p-select:hover {
  background: var(--crc-sidebar-link-hover-bg);
  border-color: var(--crc-sidebar-line);
}
.sidebar-country-switcher.p-select.p-focus {
  outline: 2px solid var(--crc-sidebar-active-icon);
  outline-offset: 2px;
  border-color: transparent;
}
.sidebar-country-switcher__value.p-select-label {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  /* Keep the country mark intact even while the copy loses its flex space. */
  min-width: 2.25rem;
  padding: 0;
  overflow: hidden;
}
.sidebar-country-switcher__mark {
  width: 2.25rem;
  height: 2.25rem;
  flex: 0 0 2.25rem;
  display: grid;
  place-items: center;
  border-radius: 0.625rem;
  border: 1px solid var(--crc-sidebar-brand-mark-border);
  background: var(--crc-sidebar-brand-mark-bg);
  color: var(--crc-sidebar-brand-mark-text);
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.03em;
}
.sidebar-country-switcher__mark .pi { font-size: 1rem; }
.sidebar-country-switcher__copy { display: grid; gap: 0.1875rem; min-width: 0; }
.sidebar-country-switcher__company {
  color: var(--crc-sidebar-text-muted);
  font-size: 0.6875rem;
  line-height: 1.3;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.sidebar-country-switcher__country {
  color: var(--crc-sidebar-text);
  font-size: 0.8125rem;
  font-weight: 650;
  line-height: 1.3;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.sidebar-country-switcher__dropdown.p-select-dropdown {
  width: 1.125rem;
  flex: 0 0 1.125rem;
  color: var(--crc-sidebar-text-muted);
}
.sidebar-country-switcher__dropdown .pi { font-size: 0.625rem; }
.sidebar-root--rail-layout .sidebar-country-switcher.p-select { width: 2.75rem; flex: 0 0 auto; }
.sidebar-root--rail-layout .sidebar-country-switcher__copy,
.sidebar-root--rail-layout .sidebar-country-switcher__dropdown { display: none; }
.sidebar-country-menu.p-select-overlay {
  min-width: 16rem !important;
  max-width: calc(100vw - 1.5rem);
  border: 1px solid var(--crc-sidebar-line-strong);
  border-radius: 0.875rem;
  background: var(--p-toolbar-background);
  color: var(--crc-sidebar-text);
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}
.sidebar-country-menu__header { display: grid; gap: 0.25rem; padding: 0.875rem 1rem; border-bottom: 1px solid var(--crc-sidebar-line); }
.sidebar-country-menu__header > span { font-size: 0.75rem; font-weight: 650; }
.sidebar-country-menu__header small { font-size: 0.6875rem; color: var(--crc-sidebar-text-muted); }
.sidebar-country-menu .p-select-list { padding: 0.375rem; gap: 0.125rem; }
.sidebar-country-menu .p-select-option { min-height: 2.75rem; padding: 0.5rem 0.625rem; gap: 0.625rem; border-radius: 0.5rem; font-size: 0.8125rem; }
.sidebar-country-menu .p-select-option-selected { background: var(--crc-sidebar-active-bg); color: var(--crc-sidebar-active-text); }
.sidebar-country-menu__badge { display: grid; place-items: center; flex: 0 0 1.875rem; width: 1.875rem; height: 1.875rem; border-radius: 0.5rem; background: var(--crc-sidebar-collapse-bg); color: var(--crc-sidebar-text-muted); font-size: 0.625rem; font-weight: 750; }
.sidebar-country-menu__badge .pi { font-size: 0.875rem; }
.sidebar-country-menu__label { flex: 1; min-width: 0; white-space: normal; overflow-wrap: anywhere; }
.sidebar-country-menu__check { margin-left: auto; color: var(--crc-sidebar-active-icon); font-size: 0.75rem; }
.sidebar-country-menu__footer { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; padding: 0.75rem 1rem; border-top: 1px solid var(--crc-sidebar-line); color: var(--crc-sidebar-text-muted); font-size: 0.625rem; }
.sidebar-country-menu__footer button { border: 0; padding: 0.125rem; background: transparent; color: var(--crc-sidebar-active-icon); cursor: pointer; }
@media (prefers-reduced-motion: reduce) { .sidebar-country-switcher.p-select { transition: none; } }
</style>
